import type {
    AgentInteractionState,
    OrchestratorAgent,
    OrchestratorState,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React, { useState } from 'react';

interface OrchestrationControlPanelProps {
    orchestrator: OrchestratorAgent;
    agents: SimpleAgentConfig;
    orchestratorState: OrchestratorState;
    agentInteractions: Map<string, AgentInteractionState>;
    onPauseOrchestration?: () => void;
    onResumeOrchestration?: () => void;
    onResetOrchestration?: () => void;
    onSkipCurrentStep?: () => void;
    onAdjustSpeed?: (speed: number) => void;
    primaryColor?: string;
}

const OrchestrationControlPanel: React.FC<OrchestrationControlPanelProps> = ({
    orchestrator,
    agents,
    orchestratorState,
    agentInteractions,
    onPauseOrchestration,
    onResumeOrchestration,
    onResetOrchestration,
    onSkipCurrentStep,
    onAdjustSpeed,
    primaryColor = '#667eea',
}) => {
    const [isPaused, setIsPaused] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

    const handlePause = () => {
        setIsPaused(true);
        onPauseOrchestration?.();
    };

    const handleResume = () => {
        setIsPaused(false);
        onResumeOrchestration?.();
    };

    const handleReset = () => {
        setIsPaused(false);
        setSpeed(1);
        setSelectedAgent(null);
        onResetOrchestration?.();
    };

    const handleSpeedChange = (newSpeed: number) => {
        setSpeed(newSpeed);
        onAdjustSpeed?.(newSpeed);
    };

    const getSystemStatus = () => {
        if (isPaused) return { status: 'Paused', color: '#f39c12', icon: '⏸️' };

        switch (orchestratorState.status) {
            case 'processing':
                return { status: 'Processing', color: primaryColor, icon: '⚡' };
            case 'synthesizing':
                return { status: 'Synthesizing', color: '#9b59b6', icon: '🔄' };
            case 'ready':
                return { status: 'Ready', color: '#27ae60', icon: '✅' };
            case 'discovering':
                return { status: 'Discovering', color: '#3498db', icon: '🔍' };
            default:
                return { status: 'Initializing', color: '#95a5a6', icon: '⚙️' };
        }
    };

    const systemStatus = getSystemStatus();
    const canPause = orchestratorState.status === 'processing' || orchestratorState.status === 'synthesizing';
    const canSkip = orchestratorState.currentStep !== null;
    const activeAgentsCount = Array.from(agentInteractions.values()).filter(
        a => a.status === 'processing' || a.status === 'responding'
    ).length;

    return (
        <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            height: '400px',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                fontSize: '1.3em',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#2c3e50',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '1.2em' }}>🎛️</span>
                    Control Panel
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.7em',
                    background: systemStatus.color,
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontWeight: 'bold',
                }}>
                    <span>{systemStatus.icon}</span>
                    {systemStatus.status}
                </div>
            </div>

            <div style={{
                height: '320px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
            }}>
                {/* Main Controls */}
                <div>
                    <h3 style={{
                        fontSize: '1em',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <span>🎮</span>
                        System Controls
                    </h3>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '10px',
                    }}>
                        {/* Pause/Resume Button */}
                        <button
                            onClick={isPaused ? handleResume : handlePause}
                            disabled={!canPause && !isPaused}
                            style={{
                                background: isPaused
                                    ? 'linear-gradient(135deg, #27ae60, #2ecc71)'
                                    : 'linear-gradient(135deg, #f39c12, #e67e22)',
                                border: 'none',
                                color: 'white',
                                padding: '12px',
                                borderRadius: '10px',
                                cursor: (!canPause && !isPaused) ? 'not-allowed' : 'pointer',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                opacity: (!canPause && !isPaused) ? 0.5 : 1,
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}
                        >
                            <span>{isPaused ? '▶️' : '⏸️'}</span>
                            {isPaused ? 'Resume' : 'Pause'}
                        </button>

                        {/* Reset Button */}
                        <button
                            onClick={handleReset}
                            style={{
                                background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
                                border: 'none',
                                color: 'white',
                                padding: '12px',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}
                        >
                            <span>🔄</span>
                            Reset
                        </button>

                        {/* Skip Step Button */}
                        <button
                            onClick={onSkipCurrentStep}
                            disabled={!canSkip}
                            style={{
                                background: canSkip
                                    ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`
                                    : 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                                border: 'none',
                                color: 'white',
                                padding: '12px',
                                borderRadius: '10px',
                                cursor: canSkip ? 'pointer' : 'not-allowed',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                opacity: canSkip ? 1 : 0.5,
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}
                        >
                            <span>⏭️</span>
                            Skip
                        </button>

                        {/* Advanced Toggle */}
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            style={{
                                background: showAdvanced
                                    ? 'linear-gradient(135deg, #9b59b6, #8e44ad)'
                                    : 'linear-gradient(135deg, #34495e, #2c3e50)',
                                border: 'none',
                                color: 'white',
                                padding: '12px',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                            }}
                        >
                            <span>⚙️</span>
                            Advanced
                        </button>
                    </div>
                </div>

                {/* Speed Control */}
                <div>
                    <h3 style={{
                        fontSize: '1em',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <span>⚡</span>
                        Execution Speed
                    </h3>

                    <div style={{
                        background: '#f8f9fa',
                        borderRadius: '10px',
                        padding: '15px',
                        border: '1px solid #e9ecef',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                        }}>
                            <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>0.5x</span>
                            <span style={{
                                fontSize: '1.1em',
                                fontWeight: 'bold',
                                color: primaryColor
                            }}>
                                {speed}x
                            </span>
                            <span style={{ fontSize: '0.9em', color: '#7f8c8d' }}>2x</span>
                        </div>

                        <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.1"
                            value={speed}
                            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                            style={{
                                width: '100%',
                                height: '6px',
                                borderRadius: '3px',
                                background: '#e9ecef',
                                outline: 'none',
                                cursor: 'pointer',
                            }}
                        />

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                            fontSize: '0.75em',
                            color: '#7f8c8d',
                        }}>
                            <span>Slower</span>
                            <span>Normal</span>
                            <span>Faster</span>
                        </div>
                    </div>
                </div>

                {/* Advanced Controls */}
                {showAdvanced && (
                    <div>
                        <h3 style={{
                            fontSize: '1em',
                            fontWeight: 'bold',
                            color: '#2c3e50',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <span>🔧</span>
                            Advanced Options
                        </h3>

                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '15px',
                            border: '1px solid #e9ecef',
                        }}>
                            {/* Agent Selection */}
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{
                                    fontSize: '0.85em',
                                    fontWeight: 'bold',
                                    color: '#2c3e50',
                                    marginBottom: '8px',
                                    display: 'block',
                                }}>
                                    Focus Agent:
                                </label>
                                <select
                                    value={selectedAgent || ''}
                                    onChange={(e) => setSelectedAgent(e.target.value || null)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        fontSize: '0.85em',
                                        background: 'white',
                                    }}
                                >
                                    <option value="">All Agents</option>
                                    <option value="orchestrator">Orchestrator</option>
                                    {agents.map(agent => (
                                        <option key={agent.id} value={agent.id}>
                                            {agent.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Debug Info */}
                            <div style={{
                                background: '#2c3e50',
                                color: '#ecf0f1',
                                padding: '10px',
                                borderRadius: '6px',
                                fontSize: '0.75em',
                                fontFamily: 'monospace',
                            }}>
                                <div>Status: {orchestratorState.status}</div>
                                <div>Active Agents: {activeAgentsCount}</div>
                                <div>Queue Length: {orchestratorState.processingQueue.length}</div>
                                <div>Current Step: {orchestratorState.currentStep?.stepId || 'None'}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Stats */}
                <div>
                    <h3 style={{
                        fontSize: '1em',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <span>📈</span>
                        Quick Stats
                    </h3>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '10px',
                    }}>
                        <div style={{
                            background: '#e8f5e8',
                            border: '1px solid #27ae60',
                            borderRadius: '8px',
                            padding: '10px',
                            textAlign: 'center',
                        }}>
                            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#27ae60' }}>
                                {Array.from(agentInteractions.values()).filter(a => a.status === 'complete').length}
                            </div>
                            <div style={{ fontSize: '0.75em', color: '#7f8c8d' }}>Completed</div>
                        </div>

                        <div style={{
                            background: '#e8f4fd',
                            border: `1px solid ${primaryColor}`,
                            borderRadius: '8px',
                            padding: '10px',
                            textAlign: 'center',
                        }}>
                            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: primaryColor }}>
                                {activeAgentsCount}
                            </div>
                            <div style={{ fontSize: '0.75em', color: '#7f8c8d' }}>Active</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrchestrationControlPanel;